import type { IObservableArray } from 'mobx';

import { type Campaign, FILTER_TARGETING_KINDS, type Targeting } from '@feathr/blackbox';

import type { TAudienceThresholdBand } from '../AdWizardAudienceStep/AdWizardAudienceStep.utils';

/** The narrowing-filter types offered on the Filters step. */
export type TNarrowingFilterType = 'location' | 'age' | 'interests';

/** The narrowing-filter types the campaign exposes on the Filters step (only Pixl+/Omni has any). */
export function getNarrowingFilterTypes(campaign: Campaign): TNarrowingFilterType[] {
  if (!campaign.isOmniCampaign) {
    return [];
  }
  return ['location', 'age', 'interests'];
}

interface IShouldShowNarrowingFilterCalloutProps {
  campaign: Campaign;
  band: TAudienceThresholdBand;
  isLoading: boolean;
  hasAudienceTargetings: boolean;
}

/** Whether the callout shows: a Pixl+/Omni campaign below/near threshold, audience set, reach ready. */
export function shouldShowNarrowingFilterCallout({
  campaign,
  band,
  isLoading,
  hasAudienceTargetings,
}: IShouldShowNarrowingFilterCalloutProps): boolean {
  if (getNarrowingFilterTypes(campaign).length === 0) {
    return false;
  }
  if (isLoading || !hasAudienceTargetings) {
    return false;
  }
  return band === 'below' || band === 'near';
}

export function validateStepFilters(
  campaign: Campaign,
  targetings: IObservableArray<Targeting>,
): string[] {
  let filterKinds = ['geo'];

  // If the campaign is pixl plus, add age demographic and interest to the filter kinds
  if (campaign.isOmniCampaign) {
    filterKinds = [...FILTER_TARGETING_KINDS];
  }

  const filters = targetings.filter(
    (t) => filterKinds.includes(t.get('kind')) && !t.get('is_archived'),
  );
  if (filters.length === 0) {
    return [];
  }

  return filters
    .map((t) => {
      const errors = t.validate(['target_data', 'kind'], false).errors;
      if (errors.length) {
        return errors[0];
      }
      return undefined;
    })
    .filter((msg) => !!msg) as string[];
}
